import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import FirestoreService from '../assets/js/firestore_instance';
import { useLocation } from 'react-router-dom';
import Preloader from './Preloader';
import { collection, query, where, getDocs } from 'firebase/firestore';
import CountUp from 'react-countup';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import GroupsSection from './GroupsSection';
import GetStarted from './GetStarted';
import AdminSection from './AdminSection';
import { motion } from 'framer-motion';
import SettingsSection from './Settings';
import ProfileSection from './ProfileSection';
import PeopleSection from './PeopleSection';


ChartJS.register(ArcElement, Tooltip, Legend);



const Dashboard = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(location.state?.activeSection || 'home');
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [organization, setOrganization] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = FirestoreService.getFirestore();
  const notSubscribed = organization?.subscription === "none" || organization?.subscription === undefined || !organization?.organizationId;

  useEffect(() => {
    const authCheck = onAuthStateChanged(auth, (user) => {
      if (user) {
        const docRef = doc(db, "admin_users", user.uid);
        getDoc(docRef)
          .then(async (doc) => {
            if (doc.exists()) {
              await getOrganization(doc.data().organizationId[0]);
              await getGroups(doc);
            }
          })
          .catch((error) => {
            console.error("Error getting document:", error);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        navigate('/sign-in');
      }
    });

    return () => authCheck();
  }, [auth, db, navigate]);

    useEffect(() => {
    console.log("Location state:", location.state);
    if (location.state?.activeSection) {
      console.log("Setting active section to:", location.state.activeSection);
      setActiveSection(location.state.activeSection);
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const getOrganization = async (orgId) => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (orgDoc.exists()) {
        setOrganization(orgDoc.data());
      } else {
        console.error("Organization not found");
      }
    } catch (error) {
      console.error("Error fetching organization:", error);
    }
  };

  const handleOrganizationUpdate = (updatedOrg) => {
    setOrganization(updatedOrg);
  };
      

  const getGroups = async (doc) => {
    try {
      const orgId = doc.data().organizationId[0];
      const groupsRef = collection(db, 'groups');
      const q = query(groupsRef, where('org_id', '==', orgId));
      const snapshot = await getDocs(q);
      var groupsList = snapshot.docs.map(doc => doc.data());
      //filter out archived groups
      groupsList = groupsList.filter(group => group.archivedAt == "" || group.archivedAt === null || group.archivedAt === undefined);
      
      setGroups(groupsList);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'home':
        return <HomeSection organization={organization} groups={groups} />;
      case 'people':
          return <PeopleSection organization={organization} groups={groups}/>;
        case 'groups':
        return <GroupsSection organization={organization} groups={groups}/>;
      case 'admin':
        return <AdminSection organization={organization} />;
      case 'profile':
        return <ProfileSection organization={organization} onUpdate={handleOrganizationUpdate}/>;
      case 'settings':
        return <SettingsSection organization={organization} />;
      default:
        return <HomeSection organization={organization} />;
    }
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="dashboard-bg min-h-screen min-w-screen flex items-center justify-center"> 
      <div className="flex h-[96vh] w-[96vw] text-gray-100 dashboard-container">
        {/* Sidebar */}
        <div className="w-1/5 dashboard-sidebar-bg flex flex-col items-center py-8 rounded-l-3xl">
          <img className="dashboard-logo" src="/assets/images/joltchat_logo_white.png" alt="JoltChat Logo" />
          <nav className="flex flex-col w-full flex-grow">
          <button
              className={`dashboard-section-tab ${activeSection === 'home' ? 'dashboard-main-color active' : ''}`}
              onClick={() => setActiveSection('home')}
            >
            <HomeIcon /> Dashboard
            </button>
            <button
              className={`dashboard-section-tab ${activeSection === 'people' ? 'dashboard-main-color active' : ''} 
                ${notSubscribed ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => organization?.organizationId && setActiveSection('people')}
              disabled={notSubscribed}
              title={notSubscribed ? "Complete setup to access people" : ""}
            >
              <PeopleIcon /> People
            </button>
            <button
              className={`dashboard-section-tab ${activeSection === 'groups' ? 'dashboard-main-color active' : ''} 
                ${notSubscribed ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => organization?.organizationId && setActiveSection('groups')}
              disabled={notSubscribed}
              title={notSubscribed ? "Complete setup to enable groups" : ""}
            >
              <GroupIcon /> Groups
            </button>
            <button
              className={`dashboard-section-tab ${activeSection === 'admin' ? 'dashboard-main-color active' : ''} 
                ${notSubscribed ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => organization?.organizationId && setActiveSection('admin')}
              disabled={notSubscribed}
              title={notSubscribed ? "Complete setup to enable admin features" : ""}
            >
              <AdminIcon /> Admins
            </button>
            <button
              className={`dashboard-section-tab ${activeSection === 'profile' ? 'dashboard-main-color active' : ''} 
                ${notSubscribed ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => organization?.organizationId && setActiveSection('profile')}
              disabled={notSubscribed}
              title={notSubscribed ? "Complete setup to edit profile" : ""}
            >
              <ProfileIcon /> Profile
            </button>
            <button
              className={`dashboard-section-tab ${activeSection === 'settings' ? 'dashboard-main-color active' : ''} 
                ${notSubscribed ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => organization?.organizationId && setActiveSection('settings')}
              disabled={notSubscribed}
              title={notSubscribed ? "Complete setup to access settings" : ""}
            >
              <SettingsIcon /> Settings
            </button>
            <button
              className={`dashboard-section-tab mt-auto ${activeSection === 'logout' ? 'dashboard-main-color active' : ''}`}
              onClick={handleSignOut}
            >
              <LogOutIcon /> Log Out
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 my-4 overflow-y-auto dashboard-main-content rounded-3xl">
            {renderSection()}
          </div>

        {/* Right Sidebar */}
        <div className="w-1/6 dashboard-sidebar-bg p-6 rounded-r-3xl">
          <div className="text-xl font-semibold mb-6">Quick Tips</div>
          <div className="space-y-4">
            <div className="p-4 bg-gray-700 rounded-lg">If a group isnt activated, you can invite them on the Groups tab!</div>
            <div className="p-4 bg-gray-700 rounded-lg">Admin users can send announcements to all your groups!</div>
            {/* <div className="p-4 bg-gray-700 rounded-lg">Example Card 3</div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeSection = ({ organization, groups = [] }) => {
  if (organization != null && organization.subscription != null && organization.subscription !== "none") {
    return (
      <div>
        <div className="flex gap-6 mb-4">
          <h1 className="text-3xl font-bold">{organization.organizationName},</h1>
          <p className='mt-2'>Welcome back</p>
        </div>
        <div className="pt-6">
          <DashboardWidget>
            <DashboardMessage />
          </DashboardWidget>
        </div>
        <div className="pt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="col-span-1">
            <DashboardWidget title="People in Groups">
              <TotalAmountOfUsersInGroups groups={groups} />
            </DashboardWidget>
          </div>
          <div className="col-span-1">
            <DashboardWidget title="Group Meeting Days">
              <GroupMeetingDays groups={groups} />
            </DashboardWidget>
          </div>
          <div className="col-span-1">
            <DashboardWidget title="Active Groups">
              <ActiveGroupAmount groups={groups} />
            </DashboardWidget>
          </div>
        </div>
      </div>
    );
  } else {
    return <GetStarted />;
  }
};

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 inline-block mr-2 mb-1">
  <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
</svg>
);

const PeopleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 inline-block mr-2 mb-1">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
</svg>

);

const GroupIcon = () =>(
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 inline-block mr-2 mb-1">
  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z" />
</svg>

);

const AdminIcon = () =>(
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 inline-block mr-2 mb-1">
  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
</svg>
);

const ProfileIcon = () =>(
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 inline-block mr-2 mb-1">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z" />
</svg>

);

const SettingsIcon = () =>(
<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 inline-block mr-2 mb-1">
  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>

);

const LogOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 inline-block mr-2 mb-1">
  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
</svg>
);

const DashboardWidget = ({ title, children }) => (
  <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
    <div className="dashboard-element-bg p-4 rounded-3xl w-full h-auto">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      <div className="flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  </motion.div>
);

// Home section components
const DashboardMessage = ({ }) => {
  return (
    <div className="w-full text-left px-4">
      <h1 className="text-3xl font-bold">Welcome to the JoltChat Dashboard</h1>
      <p className="mt-6 text-lg w-3/4">
        Here you can manage your groups, view stats, and more. 
        As we build the service, you will see many more dashboard widgets become available. 
      </p>
      <p className="mt-10 mb-7 text-xl w-3/4 font-bold">
         If you haven't already, you can get started by activating your JoltChat groups on the Groups tab.
      </p>
    </div>
  );
};
   


const ActiveGroupAmount = ({ groups = [] }) => {
  const activeGroups = groups.filter(group => group.joltActivated);
  return (
    <div className="widget-gradient-1 rounded-full w-48 h-48 flex flex-col items-center justify-center text-center p-4 my-2">
      <div>
        <h1 className="text-3xl font-bold mb-4">
          <CountUp
            end={activeGroups.length}
            duration={3}
          />
        <br />
        Active Groups</h1>
      </div>
    </div>
  );
};

const TopContributors = () => {
  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Top Contributors</h1>
      <p>View and manage your top contributors.</p>
    </div>
  );
};

const AttendanceStats = () => {
  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Attendance Stats</h1>
      <p>View and manage attendance statistics.</p>
    </div>
  );
};

const GroupMeetingDays = ({ groups = [] }) => {
  const activeGroups = groups.filter(group => group.joltActivated);
  
    const daysCounts = activeGroups.reduce((acc, group) => {
      const meetingDay = Array.isArray(group.days) && group.days.length === 0 ? 'Unspecified' : group.days || 'Unspecified';
      if (meetingDay !== '') {
        acc[meetingDay] = (acc[meetingDay] || 0) + 1;
      }
      return acc;
    }, {});

  const totalGroups = activeGroups.length;
  const percentages = Object.values(daysCounts).map(count => Math.round((count / totalGroups) * 100));
  const chartData = {
    labels: Object.keys(daysCounts),
    datasets: [
      {
        data: Object.values(daysCounts), // Use raw counts
        backgroundColor: [
          '#4ade80',
          '#22c55e',
          '#16a34a',
          '#15803d',
          '#166534',
          '#14532d',
        ],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          color: 'white',
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || 'Unspecified';
            const value = context.raw || 0;
            return `${label}: ${value}`; // Display raw count
          }
        }
      }
    },
    animation: {
      duration: 2000,
      animateRotate: true,
      animateScale: true
    }
  };

    return (
    <div className="rounded-3xl w-76 h-76 flex flex-col items-center justify-center text-center p-6">
      {Object.keys(daysCounts).length === 0 ? (
        <div className="text-gray-400 text-lg">
          No data yet
        </div>
      ) : (
        <>
          <div className="w-full h-52">
            <Pie data={chartData} options={options} />
          </div>
          <div className="mt-4">
            {Object.keys(daysCounts).map((day, index) => (
              <div key={day} className="flex justify-between w-full">
                <span className='mr-2'>{day}</span>
                <span>{percentages[index]}%</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/// This function calculates the total number of users in all groups
const TotalAmountOfUsersInGroups = ({ groups = [] }) => {
  const totalUsers = groups.reduce((acc, group) => {
    const memberCount = group?.members?.length || 0;
    return acc + memberCount;
  }, 0);

    return (
        <div className="relative flex items-center justify-center my-2">
      <div className="rounded-full w-48 h-48 flex flex-col items-center justify-center text-center p-4">
        <h1 className="text-3xl font-bold mb-4">
          <CountUp 
            end={totalUsers}
            duration={3}
          />
          <br />
          people in Groups
        </h1>
      </div>
      <svg className="absolute inset-0 w-full h-full -rotate-90">
        <circle
          className="animate-draw-circle"
          cx="96"
          cy="96"
          r="92"
          stroke="url(#gradient)"
          strokeWidth="7"
          fill="none"
          style={{
            strokeDasharray: '578.48', // Actual circumference (2 * π * 94)
            strokeDashoffset: '578.48'  // Start fully hidden
          }}
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c1c3" />
            <stop offset="50%" stopColor="#fdbb2d" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

/// This function calculates the total number of unique users across all groups
// const TotalAmountOfUsersInGroups = ({ groups = [] }) => {
//   // Create a Set to track unique member IDs
//   const uniqueMembers = new Set();
  
//   // Loop through all groups and add member IDs to the Set
//   groups.forEach(group => {
//     if (group?.members && Array.isArray(group.members)) {
//       group.members.forEach(memberId => {
//         if (memberId) uniqueMembers.add(memberId);
//       });
//     }
//   });
  
//   // The size of the Set is the count of unique members
//   const totalUniqueUsers = uniqueMembers.size;

//   return (
//     <div className="relative flex items-center justify-center my-2">
//       <div className="rounded-full w-48 h-48 flex flex-col items-center justify-center text-center p-4">
//         <h1 className="text-3xl font-bold mb-4">
//           <CountUp 
//             end={totalUniqueUsers}
//             duration={3}
//           />
//           <br />
//           unique people 
//         </h1>
//       </div>
//       <svg className="absolute inset-0 w-full h-full -rotate-90">
//         <circle
//           className="animate-draw-circle"
//           cx="96"
//           cy="96"
//           r="92"
//           stroke="url(#gradient)"
//           strokeWidth="7"
//           fill="none"
//           style={{
//             strokeDasharray: '578.48', // Actual circumference (2 * π * 94)
//             strokeDashoffset: '578.48'  // Start fully hidden
//           }}
//         />
//         <defs>
//           <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
//             <stop offset="0%" stopColor="#22c1c3" />
//             <stop offset="50%" stopColor="#fdbb2d" />
//             <stop offset="100%" stopColor="#22c55e" />
//           </linearGradient>
//         </defs>
//       </svg>
//     </div>
//   );
// }

export default Dashboard;